import React, { useEffect, useMemo, useState } from "react";
import {
  Clock,
  CheckCircle,
  AlertCircle,
  Users,
  Timer,
  Play,
  Pause,
  RotateCcw,
  User,
  LogOut,
} from "lucide-react";
import { Order, Staff } from "../types";
import { supabase } from "../lib/supabase";
import { RESTAURANT_SETTINGS_ID } from "../constants";

interface KitchenDisplayProps {
  currentStaff: Staff;
  onLogout: () => void;
}

interface RestaurantSettings {
  has_kds: boolean;
}

/**
 * Timer model that actually supports pause/resume.
 * elapsedMs = total time accumulated while running (plus previous runs)
 * lastStartMs = timestamp when current run started (only meaningful when isRunning=true)
 */
type OrderTimer = {
  orderId: string;
  elapsedMs: number;
  isRunning: boolean;
  lastStartMs: number | null;
};

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

const KitchenDisplay: React.FC<KitchenDisplayProps> = ({ currentStaff, onLogout }) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [settings, setSettings] = useState<RestaurantSettings | null>(null);
  const [filter, setFilter] = useState<"all" | "pending" | "preparing" | "ready">("all");

  const [orderTimers, setOrderTimers] = useState<Record<string, OrderTimer>>({});
  const [nowTick, setNowTick] = useState<number>(() => Date.now()); // for live display only

  useEffect(() => {
    fetchSettings();
    fetchOrders();

    // Listen for storage changes from other tabs
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "restaurant_settings" && e.newValue) {
        try {
          const newSettings = JSON.parse(e.newValue);
          setSettings(newSettings);
        } catch (error) {
          console.error("Error parsing settings from storage:", error);
        }
      }
    };
    window.addEventListener("storage", handleStorageChange);

    // Realtime: keep it simple + stable
    const ordersChannel = supabase
      .channel("kds_orders_changes")
      .on("postgres_changes", { event: "*", schema: "public", table: "orders" }, () => {
        fetchOrders();
      })
      .subscribe();

    const settingsChannel = supabase
      .channel("kds_settings_changes")
      .on("postgres_changes", { event: "*", schema: "public", table: "restaurant_settings" }, () => {
        fetchSettings();
      })
      .subscribe();

    // UI tick for clocks (timers display)
    const t = window.setInterval(() => setNowTick(Date.now()), 1000);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      ordersChannel.unsubscribe();
      settingsChannel.unsubscribe();
      window.clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ensure timers exist/auto-run for preparing orders
  useEffect(() => {
    setOrderTimers((prev) => {
      const next = { ...prev };

      // Remove timers for orders that no longer exist or are no longer preparing
      const aliveIds = new Set(orders.map((o) => o.id));
      for (const id of Object.keys(next)) {
        if (!aliveIds.has(id)) delete next[id];
      }

      // Auto-create / auto-start for preparing orders
      for (const order of orders) {
        if (order.status !== "preparing") continue;

        const existing = next[order.id];
        if (!existing) {
          next[order.id] = {
            orderId: order.id,
            elapsedMs: 0,
            isRunning: true,
            lastStartMs: Date.now(),
          };
        } else if (!existing.isRunning) {
          // keep paused state unless you want auto-resume; we won't force resume
          // (you can change this if you want)
        } else if (existing.lastStartMs == null) {
          next[order.id] = { ...existing, lastStartMs: Date.now() };
        }
      }

      return next;
    });
  }, [orders]);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from("restaurant_settings")
        .select("has_kds")
        .eq("id", RESTAURANT_SETTINGS_ID)
        .maybeSingle();

      if (error) {
        console.error("Error fetching settings:", error);
        setSettings({ has_kds: true });
      } else {
        setSettings((data as any) || { has_kds: true });
      }
    } catch (error) {
      console.error("Error:", error);
      setSettings({ has_kds: true });
    }
  };

  const fetchOrders = async () => {
    try {
      const { data, error } = await supabase
        .from("orders")
        .select(
          `
          *,
          order_items!order_items_order_id_fkey (
            *,
            menu_items!order_items_menu_item_id_fkey (*)
          )
        `
        )
        .neq("status", "completed")
        .order("created_at", { ascending: true });

      if (error) {
        console.error("Error fetching orders:", error);
        // fallback mock
        setOrders([
          {
            id: "1",
            order_number: 1001,
            table_number: 12,
            customer_name: "Emma Davis",
            order_type: "dine_in",
            status: "pending",
            total_amount: 16.99,
            payment_status: "pending",
            payment_method: "cash",
            items: [
              {
                id: "3",
                menu_item_id: "5",
                quantity: 1,
                menu_item: {
                  id: "5",
                  name: "Margherita Pizza",
                  description: "Classic pizza with tomato and mozzarella",
                  price: 16.99,
                  category: "2",
                  available: true,
                  preparation_time: 15,
                  created_at: new Date().toISOString(),
                } as any,
              } as any,
            ],
            created_at: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
            updated_at: new Date().toISOString(),
          } as any,
        ]);
      } else {
        const transformedOrders = (data as any[] | null | undefined)?.map((order) => ({
          ...order,
          items:
            order.order_items?.map((item: any) => ({
              ...item,
              menu_item: item.menu_items,
            })) || [],
        })) || [];

        setOrders(transformedOrders as any);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const updateOrderStatus = async (orderId: string, status: string) => {
    // optimistic update
    setOrders((prev) =>
      prev.map((o) => (o.id === orderId ? { ...o, status: status as any, updated_at: new Date().toISOString() } : o))
    );

    // local timer behavior
    if (status === "preparing") startOrderTimer(orderId);
    if (status !== "preparing") {
      // stop timer but keep elapsed for display if you want; here we just pause it
      pauseOrderTimer(orderId);
    }

    try {
      const { error } = await supabase
        .from("orders")
        .update({ status, updated_at: new Date().toISOString() })
        .eq("id", orderId);

      if (error) console.error("Error updating order:", error);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const startOrderTimer = (orderId: string) => {
    setOrderTimers((prev) => {
      const existing = prev[orderId];
      if (!existing) {
        return {
          ...prev,
          [orderId]: { orderId, elapsedMs: 0, isRunning: true, lastStartMs: Date.now() },
        };
      }
      if (existing.isRunning) return prev;
      return {
        ...prev,
        [orderId]: { ...existing, isRunning: true, lastStartMs: Date.now() },
      };
    });
  };

  const pauseOrderTimer = (orderId: string) => {
    setOrderTimers((prev) => {
      const t = prev[orderId];
      if (!t || !t.isRunning || t.lastStartMs == null) return prev;
      const add = Date.now() - t.lastStartMs;
      return {
        ...prev,
        [orderId]: { ...t, elapsedMs: t.elapsedMs + add, isRunning: false, lastStartMs: null },
      };
    });
  };

  const resetOrderTimer = (orderId: string) => {
    setOrderTimers((prev) => {
      const t = prev[orderId];
      if (!t) return prev;
      return {
        ...prev,
        [orderId]: { ...t, elapsedMs: 0, isRunning: true, lastStartMs: Date.now() },
      };
    });
  };

  const getTimerMs = (timer: OrderTimer) => {
    if (!timer.isRunning || timer.lastStartMs == null) return timer.elapsedMs;
    return timer.elapsedMs + (nowTick - timer.lastStartMs);
  };

  const formatMs = (ms: number) => {
    const totalSec = Math.max(0, Math.floor(ms / 1000));
    const minutes = Math.floor(totalSec / 60);
    const seconds = totalSec % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const getTimerColor = (ms: number, isRunning: boolean) => {
    if (!isRunning) return "text-gray-400";
    if (ms < 10 * 60 * 1000) return "text-green-400";
    if (ms < 20 * 60 * 1000) return "text-yellow-400";
    return "text-red-400";
  };

  const getOrderElapsedTime = (orderDate: string) => {
    const elapsed = Math.floor((Date.now() - new Date(orderDate).getTime()) / 1000);
    const minutes = Math.floor(elapsed / 60);
    const seconds = elapsed % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const getOrderElapsedColor = (orderDate: string) => {
    const elapsed = Date.now() - new Date(orderDate).getTime();
    if (elapsed < 10 * 60 * 1000) return "text-green-400";
    if (elapsed < 20 * 60 * 1000) return "text-yellow-400";
    return "text-red-400";
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 border-yellow-400 text-yellow-800";
      case "preparing":
        return "bg-blue-100 border-blue-400 text-blue-800";
      case "ready":
        return "bg-green-100 border-green-400 text-green-800";
      default:
        return "bg-gray-100 border-gray-400 text-gray-800";
    }
  };

  const filteredOrders = useMemo(() => {
    return filter === "all" ? orders : orders.filter((o) => o.status === filter);
  }, [orders, filter]);

  // If KDS is disabled
  if (settings && !settings.has_kds) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-2xl p-12 text-center max-w-md">
          <div className="bg-gray-100 p-6 rounded-2xl mb-6">
            <AlertCircle size={64} className="text-gray-400 mx-auto" />
          </div>
          <h2 className="text-3xl font-bold text-gray-800 mb-4">KDS Disabled</h2>
          <p className="text-gray-600 mb-8">
            Kitchen Display System has been disabled in restaurant settings.
          </p>
          <button
            onClick={onLogout}
            className="bg-red-600 text-white px-8 py-4 rounded-xl font-bold hover:bg-red-700 transition-colors"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Kitchen Display System</h1>
            <p className="text-green-100 mt-1">Manage orders and preparation times</p>
          </div>
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-3 bg-green-800 bg-opacity-50 px-4 py-2 rounded-xl">
              <div className="bg-green-500 p-2 rounded-full">
                <User size={18} className="text-white" />
              </div>
              <div className="text-sm">
                <div className="font-semibold text-white">{currentStaff.name}</div>
                <div className="text-green-200 capitalize text-xs">{currentStaff.role}</div>
              </div>
            </div>
            <button
              onClick={onLogout}
              className="flex items-center space-x-2 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all duration-200 transform hover:scale-105 shadow-lg"
            >
              <LogOut size={18} />
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Filters */}
        <div className="mb-6">
          <div className="flex space-x-2">
            {[
              { key: "all", label: "All Orders", color: "bg-gray-600" },
              { key: "pending", label: "Pending", color: "bg-yellow-600" },
              { key: "preparing", label: "Preparing", color: "bg-blue-600" },
              { key: "ready", label: "Ready", color: "bg-green-600" },
            ].map(({ key, label, color }) => {
              const count =
                key === "all" ? orders.length : orders.filter((o) => o.status === key).length;

              return (
                <button
                  key={key}
                  onClick={() => setFilter(key as any)}
                  className={`px-4 py-2 rounded-lg text-white font-medium transition-opacity ${
                    filter === key ? color : "bg-gray-400"
                  } ${filter !== key ? "opacity-60 hover:opacity-80" : ""}`}
                >
                  {label} ({count})
                </button>
              );
            })}
          </div>
        </div>

        {/* Orders Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredOrders.map((order) => {
            const timer = orderTimers[order.id];
            const timerMs = timer ? getTimerMs(timer) : 0;
            const timerColor = timer ? getTimerColor(timerMs, timer.isRunning) : "text-gray-400";

            return (
              <div
                key={order.id}
                className={`bg-white rounded-lg shadow-lg border-l-4 p-6 ${getStatusColor(order.status)}`}
              >
                {/* Header */}
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-bold">#{order.order_number}</h3>
                    <div className="flex items-center text-sm mt-1">
                      <Clock size={16} className="mr-1" />
                      <span className={getOrderElapsedColor(order.created_at)}>
                        {getOrderElapsedTime(order.created_at)} elapsed
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    {order.table_number && (
                      <div className="flex items-center text-sm font-semibold">
                        <Users size={16} className="mr-1" />
                        Table {order.table_number}
                      </div>
                    )}
                    <div className="text-xs text-gray-500 capitalize">{order.order_type}</div>
                  </div>
                </div>

                {/* Timer */}
                {order.status === "preparing" && (
                  <div className="mb-4 p-3 rounded-lg bg-gray-50 border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Timer size={16} className={`mr-2 ${timerColor}`} />
                        {timer ? (
                          <>
                            <span className={`font-bold ${timerColor}`}>{formatMs(timerMs)}</span>
                            <span className="ml-2 text-gray-600 text-sm">prep time</span>
                          </>
                        ) : (
                          <span className="font-bold text-blue-600">Ready to start</span>
                        )}
                      </div>

                      {timer && (
                        <div className="flex space-x-1">
                          <button
                            onClick={() => (timer.isRunning ? pauseOrderTimer(order.id) : startOrderTimer(order.id))}
                            className="p-1 text-gray-600 hover:text-gray-800"
                            title={timer.isRunning ? "Pause" : "Resume"}
                          >
                            {timer.isRunning ? <Pause size={14} /> : <Play size={14} />}
                          </button>
                          <button
                            onClick={() => resetOrderTimer(order.id)}
                            className="p-1 text-gray-600 hover:text-gray-800"
                            title="Reset"
                          >
                            <RotateCcw size={14} />
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="mt-2 text-xs text-gray-500">
                      Total time since order:{" "}
                      <span className={getOrderElapsedColor(order.created_at)}>
                        {getOrderElapsedTime(order.created_at)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Customer */}
                {order.customer_name && (
                  <div className="mb-4 p-2 bg-gray-50 rounded">
                    <span className="text-sm font-medium">Customer: {order.customer_name}</span>
                  </div>
                )}

                {/* Items */}
                <div className="mb-4 space-y-2">
                  {order.items.map((item) => (
                    <div key={item.id} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                      <div>
                        <span className="font-medium">{item.menu_item?.name}</span>
                        {item.notes && (
                          <div className="text-sm text-orange-600 font-medium">Note: {item.notes}</div>
                        )}
                      </div>
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm font-bold">
                        {item.quantity}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Actions */}
                <div className="space-y-2">
                  {order.status === "pending" && (
                    <button
                      onClick={() => updateOrderStatus(order.id, "preparing")}
                      className="w-full bg-blue-600 text-white py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                    >
                      Start Preparing
                    </button>
                  )}

                  {order.status === "preparing" && (
                    <button
                      onClick={() => updateOrderStatus(order.id, "ready")}
                      className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center justify-center"
                    >
                      <CheckCircle size={18} className="mr-2" />
                      <span>Mark Ready</span>
                      <span className="ml-3 text-xs opacity-90">
                        Prep: {timer ? formatMs(timerMs) : "0:00"}
                      </span>
                    </button>
                  )}

                  {order.status === "ready" && (
                    <button
                      onClick={() => updateOrderStatus(order.id, "completed")}
                      className="w-full bg-gray-600 text-white py-2 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
                    >
                      Complete Order
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {filteredOrders.length === 0 && (
          <div className="text-center mt-12">
            <AlertCircle size={48} className="text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No orders to display</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default KitchenDisplay;
